// Auto orders after release: the per-country volumes every plan uses (super
// admins set them), the apps that have a plan, and what their orders are
// doing. A plan saved before release launches by itself when the app reaches
// the store; one saved later waits for Launch in its editor.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { useCanEdit } from '@/components/EditOnly';
import { useToastStore } from '@/store/toast';
import {
  getApps, getAppPlan, getAutoSettings, launchAppPlan, listAutoApps, saveAppPlan, saveAutoSettings,
  type AsomAutoApp, type AsomAutoSettings, type AsomOverview, type AsomPlan,
} from '@/api/asoMarket';
import { AppLogo, Card, FieldLabel, Flags, INPUT, Notice, Stars, Toggle, fmtDate, hoursText } from './ui';
import { PlanFields } from './PlanFields';

export function AutoTab({ overview, canManage, onOpenCampaigns }: {
  overview: AsomOverview; canManage: boolean; onOpenCampaigns: () => void;
}) {
  const canEdit = useCanEdit();
  const settings = useQuery({ queryKey: ['aso-market', 'auto-settings'], queryFn: getAutoSettings });
  const apps = useQuery({ queryKey: ['aso-market', 'auto-apps'], queryFn: listAutoApps, refetchInterval: 60_000 });
  const [editing, setEditing] = useState<string | null>(null); // app id, or '' for a new plan
  const s = settings.data?.settings;

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-2xl">
            <div className="text-sm font-medium">Orders that place themselves on release</div>
            <p className="text-xs text-content-subtle mt-1">
              An app with priority countries gets local campaigns in each of them as soon as it reaches the store:
              installs via its install keyword (app name search without one) and {s?.reviewRating ?? 5}★ reviews whose texts are
              written in the country’s language and carry the review keywords. Orders above the ${overview.account.max_cost} cap
              wait for “Order anyway” on their Telegram card.
            </p>
          </div>
          {canEdit && <Button size="sm" onClick={() => setEditing('')}>+ Plan for an app</Button>}
        </div>
        {s && <SettingsForm settings={s} canManage={canManage} />}
      </Card>

      {apps.isLoading && <p className="text-sm text-content-subtle">Loading plans…</p>}
      {apps.isError && <Notice tone="danger">{(apps.error as Error).message}</Notice>}
      {apps.data && (apps.data.apps.length === 0 ? (
        <Card className="p-10 text-center">
          <p className="text-sm text-content-muted">No app has priority countries yet.</p>
          <p className="text-xs text-content-subtle">Set them when creating an app in the wizard, or add a plan here.</p>
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-content-subtle border-b border-border">
                <th className="px-4 py-2.5 font-normal">App</th>
                <th className="px-3 py-2.5 font-normal">Countries</th>
                <th className="px-3 py-2.5 font-normal">Keywords</th>
                <th className="px-3 py-2.5 font-normal">Plan</th>
                <th className="px-4 py-2.5 font-normal">Orders</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {apps.data.apps.map((a) => (
                <tr key={a.id} onClick={() => setEditing(a.id)} className="cursor-pointer hover:bg-surface-muted/60">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 min-w-[200px]">
                      <AppLogo src={a.icon_url} name={a.name} />
                      <div className="min-w-0">
                        <div className="font-medium text-content truncate max-w-[220px]">
                          {a.name}{a.is_banned && <span className="ml-1.5 text-xs text-danger font-normal">banned</span>}
                        </div>
                        <div className="text-xs text-content-subtle font-mono truncate max-w-[220px]">{a.bundle_id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3"><Flags codes={a.plan.countries} max={5} /></td>
                  <td className="px-3 py-3 text-xs text-content-muted max-w-[240px]">
                    <div className="truncate">⤓ {a.plan.install_keyword ? `“${a.plan.install_keyword}”` : <span className="text-content-subtle">app name</span>}</div>
                    <div className="truncate">✍︎ {a.plan.review_keywords.length ? a.plan.review_keywords.join(', ') : <span className="text-content-subtle">none</span>}</div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap"><PlanState app={a} /></td>
                  <td className="px-4 py-3"><OrderCounts orders={a.orders} onOpen={onOpenCampaigns} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ))}

      {editing !== null && s && (
        <PlanModal appId={editing || null} overview={overview} settings={s} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

function PlanState({ app }: { app: AsomAutoApp }) {
  if (app.state === 'launched') return <span className="text-success text-xs font-medium">Launched {fmtDate(app.launched_at)}</span>;
  if (app.state === 'waiting') return <span className="text-info text-xs font-medium">Waits for release</span>;
  if (app.released_at) return <span className="text-warning text-xs font-medium">Released: press Launch</span>;
  return <span className="text-content-subtle text-xs">—</span>;
}

const BUCKETS: [string, string, string][] = [
  ['planned', 'queued', 'text-info'],
  ['submitting', 'sending', 'text-info'],
  ['held', 'over cap', 'text-warning'],
  ['running', 'running', 'text-info'],
  ['finished', 'finished', 'text-success'],
  ['cancelled', 'cancelled', 'text-content-subtle'],
  ['rejected', 'failed', 'text-danger'],
  ['unknown', 'no answer', 'text-warning'],
  ['lost', 'lost', 'text-danger'],
  ['skipped', 'skipped', 'text-content-subtle'],
];

function OrderCounts({ orders, onOpen }: { orders: Record<string, number>; onOpen: () => void }) {
  const parts = BUCKETS.filter(([k]) => orders[k]);
  if (!parts.length) return <span className="text-content-subtle text-xs">none yet</span>;
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(); }} className="flex flex-wrap gap-x-2 text-xs hover:underline text-left">
      {parts.map(([k, label, cls]) => <span key={k} className={cls}>{orders[k]} {label}</span>)}
    </button>
  );
}

function SettingsForm({ settings, canManage }: { settings: AsomAutoSettings; canManage: boolean }) {
  const qc = useQueryClient();
  const toast = useToastStore((t) => t.push);
  const [draft, setDraft] = useState(settings);
  useEffect(() => setDraft(settings), [settings]);
  const save = useMutation({
    mutationFn: () => saveAutoSettings(draft),
    onSuccess: () => { toast('Auto order settings saved'); qc.invalidateQueries({ queryKey: ['aso-market'] }); },
    onError: (e: Error) => toast(e.message),
  });
  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);
  const num = (k: keyof AsomAutoSettings) => (e: React.ChangeEvent<HTMLInputElement>) => setDraft({ ...draft, [k]: Math.floor(Number(e.target.value) || 0) });

  if (!canManage) {
    return (
      <p className="mt-4 text-xs text-content-muted">
        {settings.enabled ? 'On' : 'Off'} · per country: {settings.installs} installs, one per {hoursText(settings.installIntervalMin / 60)};
        {' '}{settings.reviews} reviews {settings.reviewRating}★, one per {hoursText(settings.reviewIntervalMin / 60)}
      </p>
    );
  }
  return (
    <div className="mt-4 pt-4 border-t border-border space-y-3">
      <Toggle checked={draft.enabled} onChange={(enabled) => setDraft({ ...draft, enabled })}
        label="Launch plans on release" hint="Off: no plan launches by itself; Launch in a plan still works." />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 items-end">
        <div>
          <FieldLabel aside="per country">Installs</FieldLabel>
          <input className={INPUT} type="number" min={0} value={draft.installs} onChange={num('installs')} />
        </div>
        <div>
          <FieldLabel aside="min">One install every</FieldLabel>
          <input className={INPUT} type="number" min={1} value={draft.installIntervalMin} onChange={num('installIntervalMin')} />
        </div>
        <div>
          <FieldLabel aside="per country">Reviews</FieldLabel>
          <input className={INPUT} type="number" min={0} value={draft.reviews} onChange={num('reviews')} />
        </div>
        <div>
          <FieldLabel aside="min">One review every</FieldLabel>
          <input className={INPUT} type="number" min={1} value={draft.reviewIntervalMin} onChange={num('reviewIntervalMin')} />
        </div>
        <div>
          <FieldLabel>Review rating</FieldLabel>
          <div className="h-[38px] flex items-center"><Stars value={draft.reviewRating} onChange={(reviewRating) => setDraft({ ...draft, reviewRating })} /></div>
        </div>
      </div>
      <p className="text-xs text-content-subtle">
        A country takes ≈ {hoursText(draft.installs * draft.installIntervalMin / 60)} of installs and ≈ {hoursText(draft.reviews * draft.reviewIntervalMin / 60)} of reviews. 0 drops that kind.
      </p>
      <Button size="sm" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>Save settings</Button>
    </div>
  );
}

function PlanModal({ appId: initialAppId, overview, settings, onClose }: {
  appId: string | null; overview: AsomOverview; settings: AsomAutoSettings; onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToastStore((t) => t.push);
  const canEdit = useCanEdit();
  const [appId, setAppId] = useState(initialAppId);
  const [search, setSearch] = useState('');
  const apps = useQuery({ queryKey: ['aso-market', 'apps'], queryFn: getApps, enabled: !initialAppId, staleTime: 60_000 });
  const current = useQuery({ queryKey: ['aso-market', 'plan', appId], queryFn: () => getAppPlan(appId!), enabled: !!appId });
  const [plan, setPlan] = useState<AsomPlan | null>(null);
  useEffect(() => { if (current.data) setPlan(current.data.plan); }, [current.data]);

  const save = useMutation({
    mutationFn: () => saveAppPlan(appId!, plan!),
    onSuccess: (d) => {
      toast(!d.plan.countries.length ? 'Plan cleared' : d.state === 'waiting' ? 'Saved: orders go out when the app is released' : 'Plan saved');
      qc.invalidateQueries({ queryKey: ['aso-market'] });
    },
    onError: (e: Error) => toast(e.message),
  });
  const launch = useMutation({
    mutationFn: async () => { await saveAppPlan(appId!, plan!); return launchAppPlan(appId!); },
    onSuccess: (d) => { toast(`${d.queued} orders queued`); qc.invalidateQueries({ queryKey: ['aso-market'] }); onClose(); },
    onError: (e: Error) => toast(e.message),
  });

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (apps.data?.apps ?? []).filter((a) => !a.is_banned && (!q || a.name.toLowerCase().includes(q) || a.bundle_id.toLowerCase().includes(q))).slice(0, 50);
  }, [apps.data, search]);

  const d = current.data;
  const perCountry = plan ? ((plan.installs ?? settings.installs) > 0 ? 1 : 0) + ((plan.reviews ?? settings.reviews) > 0 ? 1 : 0) : 0;
  const orderCount = plan ? plan.countries.length * perCountry : 0;
  const sellable = (overview.countries ?? []).map((c) => c.code);

  return (
    <Modal isOpen onClose={onClose} title={d ? `Release plan: ${d.name}` : 'Release plan'} size="2xl">
      {!appId ? (
        <div className="space-y-2">
          <FieldLabel>App</FieldLabel>
          <input className={INPUT} placeholder="Search our Android apps…" value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
          <div className="max-h-72 overflow-y-auto border border-border rounded-md divide-y divide-border">
            {apps.isLoading && <p className="p-3 text-sm text-content-subtle">Loading apps…</p>}
            {matches.map((a) => (
              <button key={a.id} type="button" onClick={() => setAppId(a.id)} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-surface-muted">
                <AppLogo src={a.icon_url} name={a.name} size={28} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-content truncate">{a.name}</span>
                  <span className="block text-xs text-content-subtle font-mono truncate">{a.bundle_id}</span>
                </span>
                {!a.in_store && <span className="text-xs text-content-subtle">not live</span>}
              </button>
            ))}
          </div>
        </div>
      ) : current.isLoading || !plan || !d ? (
        current.isError ? <Notice tone="danger">{(current.error as Error).message}</Notice> : <p className="text-sm text-content-subtle">Loading the plan…</p>
      ) : (
        <div className="space-y-5">
          {d.state === 'launched' && <Notice tone="success">Launched {fmtDate(d.launched_at)}. Changes here do not order again; Launch again queues a new round.</Notice>}
          {d.state === 'waiting' && <Notice tone="info">Waits for the release: orders go out as soon as the app reaches the store.</Notice>}
          {!d.state && d.released_at && plan.countries.length > 0 && <Notice tone="warning">The app is already in the store, so the plan does not launch by itself: press Launch now.</Notice>}
          {!settings.enabled && <Notice tone="warning">Auto launch on release is off in the settings above.</Notice>}
          <PlanFields key={d.app_id} value={plan} onChange={setPlan} sellable={sellable} defaults={settings} />
          {orderCount > 0 && (
            <p className="text-xs text-content-subtle">
              {orderCount} campaigns: {plan.countries.length} {plan.countries.length === 1 ? 'country' : 'countries'} × {perCountry === 2 ? 'installs + reviews' : (plan.installs ?? settings.installs) > 0 ? 'installs' : 'reviews'}.
            </p>
          )}
          {canEdit && (
            <div className="flex flex-wrap gap-2 justify-end">
              {d.state !== 'waiting' && plan.countries.length > 0 && (
                <Button variant="secondary" loading={launch.isPending}
                  onClick={() => { if (confirm(`Queue ${orderCount} ASO.Market orders for ${d.name} now?`)) launch.mutate(); }}>
                  {d.state === 'launched' ? 'Launch again' : 'Launch now'}
                </Button>
              )}
              <Button loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
